import React, { useEffect } from 'react'

import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Eye as EyeIcon, Info as InfoIcon } from 'react-feather'
import { Controller, useFormContext } from 'react-hook-form'

import { FileUploadInput } from 'components'

import constants from 'constants/index'
import theme from 'theme'

const viewPassword = () => {
  const input = document.querySelector('input[name="externalSecret"]')
  if (input) {
    input.type = input.type === 'password' ? 'text' : 'password'
  }
}

const SmtpFields = ({ control, errors, isEditing, pemFileFilename }) => {
  const { watch, setValue, register } = useFormContext()
  const pemFile = watch('pemFile')
  const removePemFile = watch('removePemFile')
  const hasCurrentPemFile = !!pemFileFilename && !removePemFile

  // removePemFile não tem input visível — precisa ser registrado manualmente pro
  // react-hook-form rastrear o valor (senão setValue/watch não têm efeito nenhum).
  useEffect(() => {
    register({ name: 'removePemFile' })
  }, [register])

  const handleRemovePemFile = () => setValue('removePemFile', true)

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="textSecondary">
          Servidor SMTP
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Controller
          control={control}
          name="address"
          defaultValue=""
          as={
            <TextField
              fullWidth
              label="Servidor (host)"
              variant="outlined"
              error={!!errors.address}
              helperText={errors?.address?.message}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          name="port"
          defaultValue=""
          as={
            <TextField
              fullWidth
              type="number"
              label="Porta"
              variant="outlined"
              error={!!errors.port}
              helperText={errors?.port?.message || 'Ex: 587'}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="domain"
          defaultValue=""
          as={
            <TextField
              fullWidth
              label="Domínio"
              variant="outlined"
              error={!!errors.domain}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="authentication"
          defaultValue="login"
          as={
            <TextField
              select
              fullWidth
              label="Autenticação"
              variant="outlined"
              error={!!errors.authentication}
              helperText={errors?.authentication?.message || ''}
            >
              {constants.smtp.AUTHENTICATION_TYPES.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gridGap={theme.spacing(1)}>
          <FormControlLabel
            label="STARTTLS"
            control={
              <Controller
                control={control}
                name="starttls"
                defaultValue={true}
                valueName="checked"
                as={<Switch color="primary" />}
              />
            }
          />
          <Tooltip title={constants.smtp.INFO_MESSAGES_BY_FIELD.starttls} arrow>
            <IconButton size="small">
              <InfoIcon size={16} color={theme.palette.info.main} />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gridGap={theme.spacing(1)}>
          <FormControlLabel
            label="SSL"
            control={
              <Controller
                control={control}
                name="ssl"
                defaultValue={false}
                valueName="checked"
                as={<Switch color="primary" />}
              />
            }
          />
          <Tooltip title={constants.smtp.INFO_MESSAGES_BY_FIELD.ssl} arrow>
            <IconButton size="small">
              <InfoIcon size={16} color={theme.palette.info.main} />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gridGap={theme.spacing(1)}>
          <Typography variant="body2">
            Certificado do servidor (CA própria/autoassinada)
          </Typography>
          <Tooltip title={constants.smtp.INFO_MESSAGES_BY_FIELD.pemFile} arrow>
            <IconButton size="small">
              <InfoIcon size={16} color={theme.palette.info.main} />
            </IconButton>
          </Tooltip>
        </Box>
        {hasCurrentPemFile && !pemFile && (
          <Box
            display="flex"
            alignItems="center"
            gridGap={theme.spacing(1)}
            mb={1}
          >
            <Typography variant="caption">
              Certificado atual: {pemFileFilename}
            </Typography>
            <Button
              size="small"
              color="secondary"
              onClick={handleRemovePemFile}
            >
              Remover
            </Button>
          </Box>
        )}
        <FileUploadInput
          title="Arraste e solte ou selecione o certificado"
          controlName="pemFile"
          accept=".pem,.crt,.cer"
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" color="textSecondary">
          Acesso
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="externalKey"
          defaultValue=""
          as={
            <TextField
              fullWidth
              label="Usuário SMTP"
              variant="outlined"
              error={!!errors.externalKey}
              helperText={errors?.externalKey?.message}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="externalSecret"
          defaultValue=""
          as={
            <TextField
              fullWidth
              type="password"
              label="Senha SMTP"
              variant="outlined"
              error={!!errors.externalSecret}
              helperText={
                errors?.externalSecret?.message ||
                (isEditing ? 'Deixe em branco para manter a senha atual' : '')
              }
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="Exibir senha"
                    size="small"
                    onClick={viewPassword}
                  >
                    <EyeIcon />
                  </IconButton>
                ),
              }}
            />
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" color="textSecondary">
          Remetente
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="externalAccount"
          defaultValue=""
          as={
            <TextField
              fullWidth
              type="email"
              label="E-mail do remetente"
              variant="outlined"
              error={!!errors.externalAccount}
              helperText={
                errors?.externalAccount?.message ||
                'Endereço que aparece como remetente. Ex: no-reply@empresa.com'
              }
            />
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" color="textSecondary">
          Limites de envio (opcional)
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="throttlePerMinute"
          defaultValue=""
          as={
            <TextField
              fullWidth
              type="number"
              label="Limite por minuto"
              variant="outlined"
              error={!!errors.throttlePerMinute}
              helperText={errors?.throttlePerMinute?.message || 'Padrão 60'}
            />
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="throttlePerHour"
          defaultValue=""
          as={
            <TextField
              fullWidth
              type="number"
              label="Limite por hora"
              variant="outlined"
              error={!!errors.throttlePerHour}
              helperText={errors?.throttlePerHour?.message || 'Padrão 3000'}
            />
          }
        />
      </Grid>
    </>
  )
}

export default SmtpFields
